import type { ID } from '@octopus-do/octopus-project-core'
import { LoaderActions } from '@store/loaders'
import { CommentReadsActions } from '@store/reducers/commentReads'
import { inboxClient } from '@store/reducers/inbox'
import { managerActions } from '@store/reducers/manager/actions'
import { metaActions } from '@store/reducers/manager/meta/actions'
import type { ProjectSpace } from '@store/reducers/manager/meta/types'
import { PROJECT_HISTORY_INIT } from '@store/reducers/manager/middleware/history/types'
import { StorageActions } from '@store/reducers/manager/storage'
import { projectUsersActions } from '@store/reducers/manager/users/actions'
import { ThreadsActions } from '@store/reducers/threads'
import * as ActionTypes from '@storeOld/actions'
import axios, { type AxiosResponse } from 'axios'
import { call, cancelled, put, select } from 'redux-saga/effects'

import { authSelector } from '@/hooks/useAuth'
import { NestClient } from '@/nest'
import type { AuthState } from '@/types'

// @todo implement type of server response
const fetchProject = (
  id: ID,
  signal: AbortSignal
): Promise<AxiosResponse<any>> =>
  axios.get(`/projects/${id}`, {
    signal,
    baseURL: import.meta.env.VITE_SYNC_HTTP_URL,
    headers: {
      'X-Auth-Token': localStorage.getItem('token'),
      'X-Export-Token': localStorage.getItem('export_token'),
      'X-Password-Token': localStorage.getItem(`pw_${id}`),
    },
  })

const fetchProjectComments = (
  id: ID,
  signal: AbortSignal
): Promise<AxiosResponse<any>> =>
  axios.get(`/projects/${id}/comments`, {
    signal,
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      'X-Auth-Token': localStorage.getItem('token'),
      'X-Export-Token': localStorage.getItem('export_token'),
      'X-Password-Token': localStorage.getItem(`pw_${id}`),
    },
  })

const EMPTY_SPACE: ProjectSpace = {
  id: '',
  features: [],
  type: 'sitemap',
  workspaces: [],
}

// todo: fetch project by single request from nest
// Пространство проекта — вспомогательные данные: его ошибка не должна ронять
// загрузку проекта, поэтому глотаем её здесь
const fetchProjectSpace = (id: ID): Promise<ProjectSpace | null> =>
  NestClient.workspaces
    // The published SDK predates `type` on the space; the API already sends
    // it, so the cast goes away with the next SDK release.
    .project(id)
    .then((space) => space as unknown as ProjectSpace)
    .catch((error: unknown) => {
      console.error('Error fetching project space:', error)

      return null
    })

// Тело загрузки: кладёт проект в стор и при ошибке бросает. Политику —
// какой лоадер держать и что показать при ошибке — выбирает вызывающая сага:
// fetchProjectSaga (прямой заход на /:id) или openProjectSaga (из дашборда).
// The inbox's "changes in this project" rows count as read once the project
// is on screen (OCT-5229). Fire and forget: a miss costs one stale row
function* markProjectSeen(id: ID) {
  const auth: AuthState = yield select(authSelector)

  if (!auth.authenticated) {
    return
  }

  inboxClient.seen(id).catch((error) => {
    console.error('Error marking the project seen:', error)
  })
}

// keepUnseen: the project is loaded without being shown (Share from the
// dashboard), so its inbox rows stay as they are.
export function* loadProjectSaga(id: ID, keepUnseen = false) {
  // Отмена саги (takeLatest: пользователь кликнул другую карточку или ушёл
  // с проекта) обрывает и сами HTTP-запросы — см. finally.
  const controller = new AbortController()

  try {
    // The space call is the only one that knows whether this project has a
    // canvas at all, so it runs before the canvas is fetched. It needs a
    // session: a guest opening a shared link gets no space and falls through
    // to the normal load.
    const space: ProjectSpace | null = yield call(fetchProjectSpace, id)

    if (space) {
      yield put(metaActions.setSpace(space))

      // Nothing else runs for a report: no canvas, and no project id in meta,
      // so the sockets stay closed.
      if (space.type === 'report') {
        return
      }
    }

    // @ts-expect-error
    const projectResponse = yield call(fetchProject, id, controller.signal)

    const { data } = projectResponse
    const {
      meta: {
        access: {
          accessBlockerType,
          canView,
          canEdit,
          canComment,
          canEditContent,
        },
      },
    } = data

    yield put(metaActions.updateMeta({ id }))

    yield put(ThreadsActions.list({ id }))
    if (!keepUnseen) {
      yield call(markProjectSeen, id)
    }

    // @todo implement check version compatibility
    // const currentVersion = projectCorePackage.version
    // if (projectData.version !== currentVersion) {
    //   console.warn(
    //     `Project version mismatch: ${projectData.version} vs ${currentVersion}`
    //   )
    // }

    if (accessBlockerType) {
      yield put({
        type: metaActions.updateAccess.type,
        payload: {
          accessBlockerType,
          canView,
          canEdit,
          canComment,
          canEditContent,
        },
      })

      return
    }

    // todo: fix incorrect data meta space

    // A template is a blueprint, not a place to hold a discussion: threads on
    // it would never reach the projects made from it, because Use template
    // copies the content only. Killing canComment here switches off the
    // toolbar entry, the block modals and the socket subscription at once
    // (OCT-5044).
    const access = data.meta.template
      ? { ...data.meta.access, canComment: false }
      : data.meta.access

    yield put(
      managerActions.loadProject({
        ...data,
        meta: { ...data.meta, access, space: space || EMPTY_SPACE },
      })
    )

    yield put(StorageActions.init({ id }))

    // Read marks only mean something to a viewer who gets the comments at
    // all; asking for them as a plain viewer would only seed a baseline row
    if (access.canComment) {
      yield put(CommentReadsActions.list({ id }))
    }

    // Авторы комментариев — оформление поверх уже отрисованного проекта, а не
    // условие его открытия: запрос уходит своей сагой и лоадер не держит
    yield put(projectUsersActions.fetchUsersSaga({ id }))

    const commentsResponse: AxiosResponse = yield call(
      fetchProjectComments,
      id,
      controller.signal
    )

    yield put(managerActions.setComments(commentsResponse.data))

    yield put({
      type: PROJECT_HISTORY_INIT,
    })

    yield put(managerActions.touch({ id }))
  } finally {
    const aborted: boolean = yield cancelled()

    if (aborted) {
      controller.abort()
    }
  }
}

export function* fetchProjectSaga({
  payload: { id, silent = false },
}: ReturnType<typeof managerActions.fetchProjectSaga>) {
  try {
    if (!silent) {
      yield put(LoaderActions.set('project'))
    }

    yield call(loadProjectSaga, id)
  } catch (error) {
    console.error('Error fetching project:', error)

    yield put({
      type: ActionTypes.SET_ERROR,
      payload: {
        key: 'project',
        message: 'Error fetching project',
      },
    })
  } finally {
    yield put(LoaderActions.unset('project'))
  }
}
